/**
 * App root: router (spec §3.5 routes) with lazily loaded route chunks, the shell layout and a
 * global error boundary.
 */
import type { ComponentType } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { AppErrorBoundary } from './routes/AppErrorBoundary';
import { NotFoundPage } from './routes/NotFoundPage';
import { RouteError } from './routes/RouteError';
import { RouteLoading, Shell } from './routes/Shell';

type PageModule = { default?: ComponentType; [name: string]: unknown };

/** Route `lazy` loader picking the default export (or the named one). */
const page =
  (load: () => Promise<PageModule>, name?: string) =>
  async (): Promise<{ Component: ComponentType }> => {
    const mod = await load();
    const Component = (name ? mod[name] : undefined) ?? mod.default;
    if (!Component) throw new Error(`Route module has no component${name ? ` "${name}"` : ''}`);
    return { Component: Component as ComponentType };
  };

/**
 * Dev-only UI kit showcase. `import.meta.glob` yields an empty object when the file does not
 * exist, so a missing kit never breaks the build; in production the branch is dropped entirely.
 */
const loadKit: (() => Promise<PageModule>) | undefined = import.meta.env.DEV
  ? import.meta.glob<PageModule>('./dev/kit/KitPage.tsx')['./dev/kit/KitPage.tsx']
  : undefined;

const children: RouteObject[] = [
  { index: true, lazy: page(() => import('./routes/screens/HomePage')) },
  { path: 'r/:code', lazy: page(() => import('./routes/RoomPage')) },
  { path: 'liderlik', lazy: page(() => import('./routes/screens/LeaderboardPage')) },
  { path: 'oyun/:id', lazy: page(() => import('./routes/screens/PastGamePage')) },
];
if (loadKit) children.push({ path: 'kit', lazy: page(loadKit, 'KitPage') });
// Not lazy: `RouteError` already imports it statically, so it lives in the main chunk anyway.
children.push({ path: '*', Component: NotFoundPage });

export const routes: RouteObject[] = [
  {
    path: '/',
    Component: Shell,
    ErrorBoundary: RouteError,
    // Shown while the first lazy route chunk loads.
    HydrateFallback: RouteLoading,
    children,
  },
];

const router = createBrowserRouter(routes);

export function App() {
  return (
    <AppErrorBoundary>
      <RouterProvider router={router} />
    </AppErrorBoundary>
  );
}

export default App;
